import React, { useMemo } from 'react';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { useTheme, GTFonts, type Theme } from '../../theme';
import { formatNumberDecimal, fs, s } from '../../utils/libs';

type ComparisonMetric = {
  change: number;
  percentageChange: number;
};

type Props = {
  comparison?: ComparisonMetric | null;
  /** e.g. "last month". Renders as "vs last month" after the percentage. */
  periodLabel?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * Period-over-period delta. Same treatment as the dashboard StatCard widgets
 * so Money Collected and Sales/Expenses/Profit never disagree on the same screen.
 */
export function ComparisonChip({ comparison, periodLabel, style }: Props) {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  if (!comparison) return null;

  const change = Number(comparison.change ?? 0);
  const percentage = Number(comparison.percentageChange ?? 0);
  const showZeroInsteadOfNegativeHundred = percentage === -100;
  const displayPercentage = showZeroInsteadOfNegativeHundred ? 0 : percentage;
  const isPositive = change >= 0 || showZeroInsteadOfNegativeHundred;
  const sign = isPositive ? '+' : '-';
  const color = isPositive ? theme.colors.success : theme.colors.error;

  return (
    <View style={[styles.row, style]}>
      <Text style={[styles.percentage, { color }]}>
        {sign}
        {formatNumberDecimal(Math.abs(displayPercentage))}%
      </Text>
      {periodLabel ? (
        <Text style={styles.period}>vs {periodLabel}</Text>
      ) : null}
    </View>
  );
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(4),
      flexWrap: 'wrap',
    },
    percentage: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    period: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
  });

export default ComparisonChip;
